import React, { useState, useEffect } from 'react';

import JobProgressStatusToast from 'components/JobProgressStatusToast/JobProgressStatusToast';
import ToastMessage from 'components/ToastMessage/ToastMessage';

import { useGrid } from 'app/contexts/gridProvider';

import { GetJobProgress, GetJobProgressVariables } from 'app/graphql/generated/graphqlApolloTypes';

import useShowToast from 'app/hooks/useShowToast';

import { JobStatus } from 'app/models';

import { formatMessage } from 'utils/messages/utils';

interface BulkUpsertQuotaToastProps {
  jobInput: GetJobProgressVariables;
}

const BulkUpsertQuotaToast: React.FC<BulkUpsertQuotaToastProps> = ({ jobInput }: BulkUpsertQuotaToastProps) => {
  const [progress, setProgress] = useState(0);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const { setBulkUpsertQuotaGridFieldJobKey, setRefreshGrid, setBulkUpsertQuotaGridFields } = useGrid();
  const showToast = useShowToast();

  useEffect(() => {
    // setInterval is used to create a fake progress bar since currently
    // bulk quota upsert does not provide a way to know % of the job progress from BE
    if (jobStatus === JobStatus.PENDING) {
      const interval = setInterval(() => {
        setProgress((progress) => {
          if (progress < 0.99) {
            return progress + 0.01;
          }
          return progress;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
    if (jobStatus === JobStatus.COMPLETED) {
      setProgress(1);
      return () => {
        // No cleanup
      };
    }
    return undefined;
  }, [jobStatus]);

  return (
    <JobProgressStatusToast
      jobInput={jobInput}
      title={formatMessage('PUBLISHING_CHANGES')}
      progressDescription={formatMessage('PUBLISHING_CHANGES_MESSAGE')}
      progress={progress}
      onPollSuccess={(response: GetJobProgress, stopPolling: () => void) => {
        const status = response?.getJobProgress?.gridComputationJobOutput?.status || null;
        if (!status) {
          stopPolling();
          setBulkUpsertQuotaGridFieldJobKey(null);
        }
        setJobStatus(status as JobStatus);
        if (status === JobStatus.COMPLETED) {
          stopPolling();
          setBulkUpsertQuotaGridFieldJobKey(null);
          setRefreshGrid(true);
          showToast(
            <ToastMessage
              title={formatMessage('SUCCESSFULLY_PUBLISHED')}
              message={formatMessage('PUBLISHING_CHANGES_SUCCESS_MESSAGE')}
            />,
            'success'
          );
          setBulkUpsertQuotaGridFields({});
        } else if (status === JobStatus.FAILED) {
          stopPolling();
          setBulkUpsertQuotaGridFieldJobKey(null);
          showToast(
            <ToastMessage
              title={formatMessage('ERROR_PUBLISHING_CHANGES')}
              message={formatMessage('ERROR_PUBLISHING_CHANGES_MESSAGE')}
            />,
            'danger'
          );
        }
      }}
      data-testid="bulk-upsert-quota-status-toast"
      onPollError={() => {
        return;
      }}
    />
  );
};

export default BulkUpsertQuotaToast;
